import React, { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { CalendarClock, Paperclip, Pencil, Send, Trash2, User } from "lucide-react";
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { StateBadge, PriorityBadge } from "@/components/platform/Badges";
import { AuditTimeline } from "@/components/platform/AuditTimeline";
import { ApprovalPanel } from "@/components/platform/ApprovalPanel";
import { ApprovalRequestDialog } from "@/components/platform/ApprovalRequestDialog";
import { FileObjectCard } from "@/components/platform/FileObjectCard";
import { FileUploadDialog } from "@/components/platform/FileUploadDialog";
import { TaskFormDialog } from "@/components/platform/TaskFormDialog";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import { apiError, fmtD } from "@/lib/platform";

const Section = ({ title, children, action }) => (
  <section className="space-y-2">
    <div className="flex items-center justify-between">
      <h3 className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">{title}</h3>
      {action}
    </div>
    {children}
  </section>
);

export const TaskDetailSheet = ({ taskId, onClose, onChanged }) => {
  const auth = useAuth();
  const [task, setTask] = useState(null);
  const [events, setEvents] = useState([]);
  const [comment, setComment] = useState("");
  const [edit, setEdit] = useState(false);
  const [reqOpen, setReqOpen] = useState(false);
  const [uploadOpen, setUploadOpen] = useState(false);

  const load = useCallback(async () => {
    if (!taskId) return;
    try {
      const [t, a] = await Promise.all([
        api.get(`/platform/tasks/${taskId}`), api.get(`/platform/audit/object/task/${taskId}`),
      ]);
      setTask(t.data);
      setEvents(a.data.items);
    } catch (e) {
      toast.error(apiError(e, "Task not found"));
      onClose();
    }
  }, [taskId, onClose]);

  useEffect(() => { setTask(null); load(); }, [load]);

  const refresh = () => { load(); onChanged?.(); };

  const move = async (to) => {
    try {
      await api.post(`/platform/tasks/${taskId}/transition`, { to_state: to, comment: comment || null });
      toast.success(`Moved to ${to}`);
      setComment("");
      refresh();
    } catch (e) {
      toast.error(apiError(e, "Transition not allowed"));
    }
  };

  const remove = async () => {
    if (!window.confirm("Delete this task? This cannot be undone.")) return;
    try {
      await api.delete(`/platform/tasks/${taskId}`);
      toast.success("Task deleted");
      onChanged?.();
      onClose();
    } catch (e) {
      toast.error(apiError(e, "Delete failed"));
    }
  };

  const canEdit = auth.can("tasks", "edit");
  const pending = task?.approvals?.find((a) => a.status === "Pending");

  return (
    <Sheet open={!!taskId} onOpenChange={(o) => !o && onClose()}>
      <SheetContent className="w-full sm:max-w-[620px] overflow-y-auto" data-testid="task-detail-sheet">
        {!task ? (
          <SheetHeader className="py-20 text-center">
            <SheetTitle className="sr-only">Loading task</SheetTitle>
            <SheetDescription className="text-sm text-slate-500">Loading…</SheetDescription>
          </SheetHeader>
        ) : (
          <div className="space-y-6">
            <SheetHeader className="text-left space-y-2">
              <div className="flex items-center gap-2 flex-wrap">
                <span className="text-xs font-mono text-slate-500" data-testid="task-detail-ref">{task.task_ref}</span>
                <StateBadge state={task.status} testId="task-detail-status" />
                <PriorityBadge priority={task.priority} testId="task-detail-priority" />
                {task.is_overdue && <span className="text-[11px] font-semibold text-red-600" data-testid="task-detail-overdue">Overdue</span>}
              </div>
              <SheetTitle className="text-xl text-[#003366]" data-testid="task-detail-title">{task.title}</SheetTitle>
              <SheetDescription className="whitespace-pre-wrap">{task.description || "No description."}</SheetDescription>
              <div className="flex items-center gap-4 text-xs text-slate-600">
                <span className="flex items-center gap-1" data-testid="task-detail-owner"><User className="w-3.5 h-3.5" /> {task.owner || "Unassigned"}</span>
                <span className="flex items-center gap-1" data-testid="task-detail-due"><CalendarClock className="w-3.5 h-3.5" /> {fmtD(task.due_date)}</span>
              </div>
              <div className="flex gap-2 pt-1">
                {canEdit && <Button size="sm" variant="outline" onClick={() => setEdit(true)} data-testid="task-detail-edit"><Pencil className="w-3.5 h-3.5 mr-1" /> Edit</Button>}
                {auth.can("approvals", "create") && !pending && (
                  <Button size="sm" variant="outline" onClick={() => setReqOpen(true)} data-testid="task-detail-request-approval">
                    <Send className="w-3.5 h-3.5 mr-1" /> Request approval
                  </Button>
                )}
                {auth.can("tasks", "delete") && (
                  <Button size="sm" variant="ghost" className="text-red-600 ml-auto" onClick={remove} data-testid="task-detail-delete">
                    <Trash2 className="w-3.5 h-3.5 mr-1" /> Delete
                  </Button>
                )}
              </div>
            </SheetHeader>

            <Section title="Linked records">
              <div className="flex flex-wrap gap-1.5" data-testid="task-detail-links">
                {task.links?.length ? task.links.map((l) => (
                  <span key={`${l.entity_type}-${l.entity_id}`} className="px-2 py-0.5 rounded-full bg-slate-100 text-[11px] text-slate-700">
                    <span className="font-semibold uppercase text-[10px] text-slate-500 mr-1">{l.type_label}</span>{l.label}
                  </span>
                )) : <span className="text-xs text-slate-400">None</span>}
              </div>
            </Section>

            {canEdit && task.allowed_next?.length > 0 && (
              <Section title="Move workflow">
                <Input value={comment} onChange={(e) => setComment(e.target.value)} placeholder="Optional comment for this transition"
                       className="h-9 text-xs" data-testid="task-transition-comment" />
                <div className="flex flex-wrap gap-1.5">
                  {task.allowed_next.map((s) => (
                    <Button key={s} size="sm" variant="outline" className="h-8 text-xs" onClick={() => move(s)}
                            data-testid={`task-transition-${s.toLowerCase().replace(/\s+/g, "-")}`}>
                      → {s}
                    </Button>
                  ))}
                </div>
              </Section>
            )}

            <Section title={`Approvals (${task.approvals?.length || 0})`}>
              {task.approvals?.length ? task.approvals.map((a) => (
                <ApprovalPanel key={a.id} approval={{ ...a, can_decide: false }} currentUser={auth.user?.username} onChanged={refresh} />
              )) : <div className="text-xs text-slate-400">No approvals requested.</div>}
              {pending && <div className="text-[11px] text-slate-500">Approvers decide from the Approvals inbox.</div>}
            </Section>

            <Section title={`Attachments (${task.files?.length || 0})`} action={auth.can("files", "create") && (
              <Button size="sm" variant="ghost" className="h-7 text-xs" onClick={() => setUploadOpen(true)} data-testid="task-detail-attach">
                <Paperclip className="w-3.5 h-3.5 mr-1" /> Attach
              </Button>
            )}>
              <div className="space-y-2">
                {task.files?.map((f) => <FileObjectCard key={f.id} file={f} onChanged={refresh} />)}
              </div>
            </Section>

            <Section title="Activity timeline">
              <AuditTimeline items={events} compact testId="task-detail-timeline" />
            </Section>
          </div>
        )}
        <TaskFormDialog open={edit} onOpenChange={setEdit} task={task} onSaved={refresh} />
        {task && (
          <ApprovalRequestDialog open={reqOpen} onOpenChange={setReqOpen}
                                 target={{ module: "tasks", object_type: "task", object_id: task.id, title: task.title }}
                                 onRequested={refresh} />
        )}
        {task && (
          <FileUploadDialog open={uploadOpen} onOpenChange={setUploadOpen} entity={{ type: "task", id: task.id, module: "tasks" }}
                            onUploaded={refresh} />
        )}
      </SheetContent>
    </Sheet>
  );
};
